import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ticketService from '../services/ticketService';
import merchantService from '../services/merchantService';
import Pagination from '../components/Pagination';
import { EyeIcon, SearchIcon } from '../components/Icons';
import ProviderChangeConfigFields, { buildDefaultProviderChangeConfig, buildExistingProviderChangeConfig, mergeProviderChangeConfig } from '../components/tickets/ProviderChangeConfigFields';
import RecordUpdateFields from '../components/tickets/RecordUpdateFields';
import RecordCreationFields from '../components/tickets/RecordCreationFields';
import TicketMerchantSelect from '../components/tickets/TicketMerchantSelect';
import TicketAssigneeSelect from '../components/tickets/TicketAssigneeSelect';
import { isRecordCreationTicketType, getRecordCreationLevel, getRecordCreationRequestKey } from '../utils/recordCreationTickets';
import ProviderSelectOption, { getProviderOptionLabel } from '../components/tickets/ProviderSelectOption';
import { formatRate, getTicketQueueItem } from '../utils/ticketQueue';
import { getTicketProviderDefinition } from '../utils/ticketRequests';
import './Tickets.css';

const DEFAULT_STAGES = [
  { value: 'developer_setup', label: 'IT Setup' },
  { value: 'am_credentials', label: 'AM Credentials' },
  { value: 'am_testing', label: 'AM Testing' },
  { value: 'it_fixing', label: 'IT Fixing' },
  { value: 'qa_testing', label: 'QA Testing' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' }
];

const DEFAULT_STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'blocked', label: 'Blocked / Needs info' },
  { value: 'pending_review', label: 'Ready for review' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'approved', label: 'Approved / Pending application' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'applying', label: 'Applying' }
];

const COSTING_RATE_TICKET_TYPE = 'costing_rate_change';
const PROVIDER_CHANGE_TICKET_TYPES = ['add_provider', 'change_provider', 'remove_provider'];

const isProviderChangeTicketType = (ticketType) => PROVIDER_CHANGE_TICKET_TYPES.includes(ticketType);

const TicketUser = ({ name, fallback }) => (
  <span className={name ? 'tickets-person' : 'tickets-unset'} title={name || undefined}>{name || fallback}</span>
);

const Tickets = () => {
  const navigate = useNavigate();
  const { admin, hasPermission, getDefaultRoute } = useAuth();
  const canView = hasPermission('tickets', 'view');
  const canCreate = hasPermission('tickets', 'create');
  const canEdit = hasPermission('tickets', 'edit');
  const canAssign = hasPermission('tickets', 'assign');
  const canComment = hasPermission('tickets', 'comment');
  const canApproveCostingRate = ['finance', 'super_admin'].includes(admin?.role) && hasPermission('master_providers', 'edit_financial');
  const canOpenTickets = canView || canCreate || canEdit || canAssign || canComment || canApproveCostingRate || ['finance', 'developer'].includes(admin?.role);

  const [tickets, setTickets] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [merchantProviders, setMerchantProviders] = useState([]);
  const [costingProviders, setCostingProviders] = useState([]);
  const [targetProviders, setTargetProviders] = useState([]);
  const [selectedCostingProvider, setSelectedCostingProvider] = useState(null);
  const [selectedTargetProvider, setSelectedTargetProvider] = useState(null);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [merchantProvidersError, setMerchantProvidersError] = useState('');
  const [loadingCostingProviders, setLoadingCostingProviders] = useState(false);
  const [loadingTargetProviders, setLoadingTargetProviders] = useState(false);
  const [merchantSearch, setMerchantSearch] = useState('');
  const [providerSearch, setProviderSearch] = useState('');
  const [costingProviderSearch, setCostingProviderSearch] = useState('');
  const [targetProviderSearch, setTargetProviderSearch] = useState('');
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [providerChangeConfig, setProviderChangeConfig] = useState(null);
  const [recordRequest, setRecordRequest] = useState({ module: '', data: {} });
  const [recordOptions, setRecordOptions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const createDialogRef = useRef(null);
  const createTypeRef = useRef(null);
  useEffect(() => {
    const dialog = createDialogRef.current;
    if (showCreateModal && dialog && !dialog.open) {
      dialog.showModal();
      createTypeRef.current?.focus();
    }
  }, [showCreateModal]);
  useEffect(() => {
    if (createError) createDialogRef.current?.querySelector('[role="alert"]')?.scrollIntoView({ block: 'nearest' });
  }, [createError]);
  const [createForm, setCreateForm] = useState({
    ticket_type: 'manual',
    provider_kind: 'game',
    title: '',
    description: '',
    merchant_id: '',
    master_provider_id: '',
    from_master_provider_id: '',
    to_master_provider_id: '',
    requested_rate: '',
    priority: 'normal',
    assigned_to_user: '',
    due_date: ''
  });
  const [filters, setFilters] = useState({ search: '', ticket_type: '', status: '', assigned_to_user: '' });
  const [workflowMeta, setWorkflowMeta] = useState({
    stages: DEFAULT_STAGES,
    statuses: DEFAULT_STATUSES,
    stage_labels: Object.fromEntries(DEFAULT_STAGES.map(item => [item.value, item.label])),
    status_labels: Object.fromEntries(DEFAULT_STATUSES.map(item => [item.value, item.label]))
  });

  const fetchTickets = useCallback(async (page = 1, limit = pagination.limit) => {
    if (!canOpenTickets) return;
    setLoading(true);
    const res = await ticketService.getTickets({ page, limit, ...filters });
    if (res.success) {
      setTickets(res.data);
      setPagination(res.pagination);
    }
    setLoading(false);
  }, [canOpenTickets, filters, pagination.limit]);

  useEffect(() => {
    if (!canOpenTickets) {
      navigate(getDefaultRoute(), { replace: true });
      return;
    }
    const timer = setTimeout(() => fetchTickets(1), 300);
    return () => clearTimeout(timer);
  }, [canOpenTickets, fetchTickets, getDefaultRoute, navigate]);

  useEffect(() => {
    if (!canOpenTickets) return;
    ticketService.getWorkflow().then(res => {
      if (res.success) setWorkflowMeta(res.data);
    });
    ticketService.getAssignees().then(res => {
      if (res.success) setAssignees(res.data);
    });
  }, [canOpenTickets]);

  useEffect(() => {
    if (!showCreateModal || ['l0_create', 'record_update', COSTING_RATE_TICKET_TYPE].includes(createForm.ticket_type) || merchants.length > 0 ||
      (createForm.ticket_type === 'l1_create' && !hasPermission('merchants', 'view'))) return;
    setLoadingMerchants(true);
    merchantService.getMerchants({ page: 1, limit: 1000 }).then(res => {
      if (res.success) setMerchants(res.data || []);
    }).finally(() => setLoadingMerchants(false));
  }, [createForm.ticket_type, showCreateModal, merchants.length, hasPermission]);

  useEffect(() => {
    let active = true;
    setMerchantProviders([]);
    setMerchantProvidersError('');
    if (!showCreateModal || isRecordCreationTicketType(createForm.ticket_type) || ['record_update', COSTING_RATE_TICKET_TYPE].includes(createForm.ticket_type) || !createForm.merchant_id) {
      setLoadingProviders(false);
      return;
    }

    setLoadingProviders(true);
    const request = createForm.provider_kind === 'payment_gateway'
      ? merchantService.getMerchantById(createForm.merchant_id)
      : merchantService.getMerchantProviders(createForm.merchant_id, { page: 1, limit: 1000 });
    request.then(res => {
      if (!active) return;
      if (res.success) setMerchantProviders(createForm.provider_kind === 'payment_gateway' ? res.data.payment_gateways || [] : res.data || []);
      else setMerchantProvidersError('Unable to check merchant providers');
    }).catch(() => {
      if (active) setMerchantProvidersError('Unable to check merchant providers');
    }).finally(() => {
      if (active) setLoadingProviders(false);
    });

    return () => { active = false; };
  }, [createForm.ticket_type, createForm.provider_kind, showCreateModal, createForm.merchant_id]);

  useEffect(() => {
    if (!showCreateModal || createForm.ticket_type !== COSTING_RATE_TICKET_TYPE) return;

    const timer = setTimeout(() => {
      setLoadingCostingProviders(true);
      ticketService.getCostingRateProviders({ page: 1, limit: 100, search: costingProviderSearch.trim() }).then(res => {
        if (res.success) {
          setCostingProviders(res.data || []);
          return;
        }
        setCostingProviders([]);
      }).finally(() => setLoadingCostingProviders(false));
    }, 250);

    return () => clearTimeout(timer);
  }, [costingProviderSearch, createForm.ticket_type, showCreateModal]);

  useEffect(() => {
    let active = true;
    setTargetProviders([]);
    if (!showCreateModal || !createForm.merchant_id || createForm.ticket_type !== 'add_provider') {
      setLoadingTargetProviders(false);
      return;
    }

    setLoadingTargetProviders(true);
    const timer = setTimeout(() => {
      ticketService.getProviderOptions({
        merchant_id: createForm.merchant_id,
        provider_kind: createForm.provider_kind,
        search: targetProviderSearch.trim()
      }).then(res => {
        if (active) setTargetProviders(res.success ? res.data || [] : []);
      }).catch(() => {
        if (active) setTargetProviders([]);
      }).finally(() => {
        if (active) setLoadingTargetProviders(false);
      });
    }, 250);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [createForm.merchant_id, createForm.ticket_type, createForm.provider_kind, showCreateModal, targetProviderSearch]);

  const formatLabel = (value) => {
    if (workflowMeta.stage_labels?.[value]) return workflowMeta.stage_labels[value];
    if (workflowMeta.status_labels?.[value]) return workflowMeta.status_labels[value];
    return String(value || '-').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };
  const formatTicketLabel = (value, ticket) => (
    ticket?.workflow?.stage_labels?.[value] ||
    ticket?.workflow?.status_labels?.[value] ||
    formatLabel(value)
  );
  const getTicketStatusLabel = (ticket) => ticket?.workflow?.current_status_label || formatTicketLabel(ticket.status, ticket);
  const getMerchantLabel = useCallback((merchant) => (
    [merchant?.name, merchant?.merchant_code || merchant?.client_code].filter(Boolean).join(' / ') || 'Merchant'
  ), []);
  const getProviderItem = useCallback((item) => item?.provider_id || {}, []);
  const getProviderLabel = useCallback((item) => getProviderOptionLabel(getProviderItem(item)), [getProviderItem]);
  const getAssigneeLabel = useCallback((user) => user?.username || user?.email || 'Assignee', []);
  const selectedMerchant = useMemo(
    () => merchants.find(merchant => merchant._id === createForm.merchant_id) || null,
    [merchants, createForm.merchant_id]
  );
  const selectedProvider = useMemo(
    () => merchantProviders.find(item => getProviderItem(item)._id === createForm.master_provider_id) || null,
    [merchantProviders, createForm.master_provider_id, getProviderItem]
  );
  const selectedFromProvider = useMemo(
    () => merchantProviders.find(item => getProviderItem(item)._id === createForm.from_master_provider_id) || null,
    [merchantProviders, createForm.from_master_provider_id, getProviderItem]
  );
  const selectedAssignee = useMemo(
    () => assignees.find(user => user._id === createForm.assigned_to_user) || null,
    [assignees, createForm.assigned_to_user]
  );
  const filteredMerchants = useMemo(() => {
    const query = merchantSearch.trim().toLowerCase();
    if (!query) return merchants;
    return merchants.filter(merchant => (
      getMerchantLabel(merchant).toLowerCase().includes(query) ||
      String(merchant.merchant_code || '').toLowerCase().includes(query) ||
      String(merchant.client_code || '').toLowerCase().includes(query)
    ));
  }, [merchants, merchantSearch, getMerchantLabel]);
  const filteredProviders = useMemo(() => {
    const query = providerSearch.trim().toLowerCase();
    if (!query) return merchantProviders;
    return merchantProviders.filter(item => {
      const provider = getProviderItem(item);
      return (
        getProviderLabel(item).toLowerCase().includes(query) ||
        String(provider.code || '').toLowerCase().includes(query) ||
        String(provider.type || '').toLowerCase().includes(query)
      );
    });
  }, [merchantProviders, providerSearch, getProviderItem, getProviderLabel]);
  const filteredAssignees = useMemo(() => {
    const query = assigneeSearch.trim().toLowerCase();
    if (!query) return assignees;
    return assignees.filter(user => (
      getAssigneeLabel(user).toLowerCase().includes(query) ||
      String(user.email || '').toLowerCase().includes(query) ||
      String(user.role || '').toLowerCase().includes(query)
    ));
  }, [assignees, assigneeSearch, getAssigneeLabel]);
  const configuredProviderIds = useMemo(() => new Set(
    merchantProviders.map(item => getProviderItem(item)._id).filter(Boolean)
  ), [merchantProviders, getProviderItem]);
  const merchantSearchMatchesSelection = selectedMerchant && merchantSearch.trim().toLowerCase() === getMerchantLabel(selectedMerchant).toLowerCase();
  const activeExistingProvider = createForm.ticket_type === 'change_provider' || createForm.ticket_type === 'remove_provider'
    ? selectedFromProvider
    : selectedProvider;
  const providerSearchMatchesSelection = activeExistingProvider && providerSearch.trim().toLowerCase() === getProviderLabel(activeExistingProvider).toLowerCase();
  const costingProviderSearchMatchesSelection = selectedCostingProvider && costingProviderSearch.trim().toLowerCase() === getProviderOptionLabel(selectedCostingProvider).toLowerCase();
  const targetProviderSearchMatchesSelection = selectedTargetProvider && targetProviderSearch.trim().toLowerCase() === getProviderOptionLabel(selectedTargetProvider).toLowerCase();
  const providerToConfigure = createForm.ticket_type === 'change_provider'
    ? getTicketProviderDefinition(selectedFromProvider ? { ...getProviderItem(selectedFromProvider), callback_urls: selectedFromProvider.callback_urls || [] } : null, createForm.provider_kind === 'payment_gateway')
    : getTicketProviderDefinition(selectedTargetProvider, createForm.provider_kind === 'payment_gateway');
  const recordProvider = getTicketProviderDefinition(recordRequest.data, recordRequest.module === 'payment_gateway_master_providers', recordOptions);
  const assigneeSearchMatchesSelection = selectedAssignee && assigneeSearch.trim().toLowerCase() === getAssigneeLabel(selectedAssignee).toLowerCase();

  const updateCreateForm = (field, value) => {
    setCreateError('');
    setCreateForm(prev => ({ ...prev, [field]: value }));
  };

  const updateCreateTicketType = (selectedType) => {
    const paymentGateway = ['add_payment_gateway', 'change_payment_gateway', 'remove_payment_gateway'].includes(selectedType);
    const ticketType = paymentGateway ? selectedType.replace('_payment_gateway', '_provider') : selectedType;
    setCreateError('');
    setRecordRequest({ module: '', data: {} });
    setCreateForm(prev => ({
      ...prev,
      ticket_type: ticketType,
      provider_kind: paymentGateway ? 'payment_gateway' : 'game',
      title: ticketType === COSTING_RATE_TICKET_TYPE ? '' : prev.title,
      merchant_id: '',
      master_provider_id: '',
      from_master_provider_id: '',
      to_master_provider_id: '',
      requested_rate: '',
      assigned_to_user: ticketType === COSTING_RATE_TICKET_TYPE ? '' : prev.assigned_to_user,
      due_date: ticketType === COSTING_RATE_TICKET_TYPE ? '' : prev.due_date
    }));
    setMerchantProviders([]);
    setMerchantSearch('');
    setProviderSearch('');
    setCostingProviderSearch('');
    setTargetProviderSearch('');
    setSelectedCostingProvider(null);
    setSelectedTargetProvider(null);
    setProviderChangeConfig(null);
    if (ticketType === COSTING_RATE_TICKET_TYPE) setAssigneeSearch('');
  };

  const updateMerchantSearch = (value) => {
    setCreateError('');
    setMerchantSearch(value);
    if (createForm.merchant_id && value.trim().toLowerCase() !== getMerchantLabel(selectedMerchant).toLowerCase()) {
      setCreateForm(prev => ({
        ...prev,
        merchant_id: '',
        master_provider_id: '',
        from_master_provider_id: '',
        to_master_provider_id: ''
      }));
      setProviderSearch('');
      setTargetProviderSearch('');
      setSelectedTargetProvider(null);
      setProviderChangeConfig(null);
    }
  };

  const updateProviderSearch = (value) => {
    setCreateError('');
    setProviderSearch(value);
    if (!isProviderChangeTicketType(createForm.ticket_type) && createForm.master_provider_id && value.trim().toLowerCase() !== getProviderLabel(selectedProvider).toLowerCase()) {
      setCreateForm(prev => ({
        ...prev,
        master_provider_id: ''
      }));
    }
    if (createForm.from_master_provider_id && value.trim().toLowerCase() !== getProviderLabel(selectedFromProvider).toLowerCase()) {
      setProviderChangeConfig(null);
      setCreateForm(prev => ({
        ...prev,
        from_master_provider_id: '',
        master_provider_id: '',
        to_master_provider_id: ''
      }));
    }
  };

  const selectCreateMerchant = (merchant) => {
    setCreateError('');
    setMerchantSearch(getMerchantLabel(merchant));
    setProviderSearch('');
    setCreateForm(prev => ({
      ...prev,
      merchant_id: merchant._id,
      master_provider_id: '',
      from_master_provider_id: '',
      to_master_provider_id: ''
    }));
    setSelectedTargetProvider(null);
    setTargetProviderSearch('');
    setProviderChangeConfig(null);
  };

  const selectCreateProvider = (item) => {
    const provider = getProviderItem(item);
    setCreateError('');
    setProviderSearch(getProviderLabel(item));
    setCreateForm(prev => ({
      ...prev,
      master_provider_id: provider._id,
      from_master_provider_id: ['change_provider', 'remove_provider'].includes(prev.ticket_type) ? provider._id : prev.from_master_provider_id,
      to_master_provider_id: prev.ticket_type === 'change_provider' ? provider._id : prev.to_master_provider_id
    }));
    if (createForm.ticket_type === 'change_provider') {
      setProviderChangeConfig(buildExistingProviderChangeConfig(getTicketProviderDefinition({ ...provider, callback_urls: item.callback_urls || [] }, createForm.provider_kind === 'payment_gateway'), item));
    }
  };

  const updateTargetProviderSearch = (value) => {
    setCreateError('');
    setTargetProviderSearch(value);
    const selectedLabel = selectedTargetProvider ? getProviderOptionLabel(selectedTargetProvider).toLowerCase() : '';
    if (createForm.to_master_provider_id && value.trim().toLowerCase() !== selectedLabel) {
      setSelectedTargetProvider(null);
      setProviderChangeConfig(null);
      setCreateForm(prev => ({
        ...prev,
        to_master_provider_id: '',
        master_provider_id: ''
      }));
    }
  };

  const selectCreateTargetProvider = (provider) => {
    setCreateError('');
    setSelectedTargetProvider(provider);
    setTargetProviderSearch(getProviderOptionLabel(provider));
    setProviderChangeConfig(buildDefaultProviderChangeConfig(getTicketProviderDefinition(provider, createForm.provider_kind === 'payment_gateway')));
    setCreateForm(prev => ({
      ...prev,
      to_master_provider_id: provider._id,
      master_provider_id: provider._id
    }));
  };

  const updateCostingProviderSearch = (value) => {
    setCreateError('');
    setCostingProviderSearch(value);
    const selectedLabel = selectedCostingProvider ? getProviderOptionLabel(selectedCostingProvider).toLowerCase() : '';
    if (createForm.master_provider_id && value.trim().toLowerCase() !== selectedLabel) {
      setSelectedCostingProvider(null);
      setCreateForm(prev => ({
        ...prev,
        master_provider_id: '',
        requested_rate: ''
      }));
    }
  };

  const selectCreateCostingProvider = (provider) => {
    setCreateError('');
    setSelectedCostingProvider(provider);
    setCostingProviderSearch(getProviderOptionLabel(provider));
    setCreateForm(prev => ({
      ...prev,
      master_provider_id: provider._id
    }));
  };

  const updateAssigneeSearch = (value) => {
    setCreateError('');
    setAssigneeSearch(value);
    if (createForm.assigned_to_user && value.trim().toLowerCase() !== getAssigneeLabel(selectedAssignee).toLowerCase()) {
      setCreateForm(prev => ({
        ...prev,
        assigned_to_user: ''
      }));
    }
  };

  const selectCreateAssignee = (user) => {
    setCreateError('');
    setAssigneeSearch(getAssigneeLabel(user));
    setCreateForm(prev => ({
      ...prev,
      assigned_to_user: user._id
    }));
  };

  const resetCreateForm = () => {
    setRecordRequest({ module: '', data: {} });
    setCreateForm({
      ticket_type: 'manual',
      provider_kind: 'game',
      title: '',
      description: '',
      merchant_id: '',
      master_provider_id: '',
      from_master_provider_id: '',
      to_master_provider_id: '',
      requested_rate: '',
      priority: 'normal',
      assigned_to_user: '',
      due_date: ''
    });
    setMerchantProviders([]);
    setMerchantSearch('');
    setProviderSearch('');
    setCostingProviderSearch('');
    setTargetProviderSearch('');
    setSelectedCostingProvider(null);
    setSelectedTargetProvider(null);
    setProviderChangeConfig(null);
    setAssigneeSearch('');
    setCreateError('');
  };

  const handleCreateTicket = async () => {
    if (createForm.ticket_type === 'record_update') {
      if (!recordRequest.record_id) { setCreateError('Select the record to update.'); return; }
      setCreating(true);
      setCreateError('');
      const result = await ticketService.createTicket({ ticket_type: 'record_update', record_update: recordRequest, description: createForm.description.trim() });
      setCreating(false);
      if (result.success) { resetCreateForm(); setShowCreateModal(false); fetchTickets(1); navigate(`/tickets/${result.data._id}`); }
      else setCreateError(result.message || 'Unable to request the record change.');
      return;
    }
    if (isRecordCreationTicketType(createForm.ticket_type)) {
      if (!recordRequest.module || !createForm.assigned_to_user) {
        setCreateError('Select the category and initial owner.');
        return;
      }
      setCreating(true);
      setCreateError('');
      const res = await ticketService.createTicket({
        ticket_type: createForm.ticket_type, [getRecordCreationRequestKey(createForm.ticket_type)]: {
          ...recordRequest,
          ...(createForm.ticket_type === 'l1_create' && createForm.merchant_id
            ? { merchant_provider_config: mergeProviderChangeConfig(recordProvider, providerChangeConfig || {}) } : {})
        },
        ...(createForm.ticket_type === 'l1_create' ? { merchant_id: createForm.merchant_id || null } : {}),
        description: createForm.description.trim(), assigned_to_user: createForm.assigned_to_user,
        priority: createForm.priority, due_date: createForm.due_date || null
      });
      setCreating(false);
      if (res.success) {
        resetCreateForm();
        setShowCreateModal(false);
        fetchTickets(1);
        navigate(`/tickets/${res.data._id}`);
      } else setCreateError(res.message || 'Failed to create record request.');
      return;
    }
    if (isProviderChangeTicketType(createForm.ticket_type)) {
      if (!createForm.merchant_id) {
        setCreateError('Select a merchant.');
        return;
      }
      if (createForm.ticket_type === 'add_provider' && !createForm.assigned_to_user) {
        setCreateError('Select the initial owner for provider setup.');
        return;
      }
      if (['change_provider', 'remove_provider'].includes(createForm.ticket_type) && !createForm.from_master_provider_id) {
        setCreateError('Select the current merchant provider.');
        return;
      }
      if (['add_provider', 'change_provider'].includes(createForm.ticket_type) && !createForm.to_master_provider_id) {
        setCreateError('Select a provider.');
        return;
      }
      if (['add_provider', 'change_provider'].includes(createForm.ticket_type) && !providerChangeConfig) {
        setCreateError('Configure the provider information.');
        return;
      }

      setCreating(true);
      setCreateError('');
      const res = await ticketService.createTicket({
        ticket_type: createForm.ticket_type,
        provider_kind: createForm.provider_kind,
        title: createForm.title.trim(),
        description: createForm.description.trim(),
        merchant_id: createForm.merchant_id,
        master_provider_id: createForm.to_master_provider_id || createForm.from_master_provider_id,
        from_master_provider_id: createForm.from_master_provider_id || null,
        to_master_provider_id: createForm.to_master_provider_id || null,
        provider_config: providerChangeConfig,
        priority: createForm.priority,
        ...(createForm.ticket_type === 'add_provider' ? { assigned_to_user: createForm.assigned_to_user } : {}),
        due_date: createForm.due_date || null
      });
      setCreating(false);
      if (res.success) {
        resetCreateForm();
        setShowCreateModal(false);
        fetchTickets(1);
        navigate(`/tickets/${res.data._id}`);
        return;
      }
      setCreateError(res.message || 'Failed to create provider change ticket.');
      return;
    }

    if (createForm.ticket_type === COSTING_RATE_TICKET_TYPE) {
      const requestedRate = Number(createForm.requested_rate);
      if (!createForm.master_provider_id) {
        setCreateError('Select an L1 provider.');
        return;
      }
      if (!Number.isFinite(requestedRate) || requestedRate < 0 || requestedRate > 100) {
        setCreateError('Requested costing rate must be between 0 and 100.');
        return;
      }

      setCreating(true);
      setCreateError('');
      const res = await ticketService.createTicket({
        ticket_type: COSTING_RATE_TICKET_TYPE,
        master_provider_id: createForm.master_provider_id,
        requested_rate: requestedRate,
        description: createForm.description.trim()
      });
      setCreating(false);
      if (res.success) {
        resetCreateForm();
        setShowCreateModal(false);
        fetchTickets(1);
        navigate(`/tickets/${res.data._id}`);
        return;
      }
      setCreateError(res.message || 'Failed to create costing rate ticket.');
      return;
    }

    if (!createForm.title.trim()) {
      setCreateError('Ticket name is required.');
      return;
    }
    setCreating(true);
    setCreateError('');
    const payload = {
      ...createForm,
      title: createForm.title.trim(),
      description: createForm.description.trim(),
      merchant_id: createForm.merchant_id || null,
      master_provider_id: createForm.master_provider_id || null
    };
    const res = await ticketService.createTicket(payload);
    setCreating(false);
    if (res.success) {
      resetCreateForm();
      setShowCreateModal(false);
      fetchTickets(1);
      navigate(`/tickets/${res.data._id}`);
      return;
    }
    setCreateError(res.message || 'Failed to create ticket.');
  };

  const updateFilters = (patch) => {
    setFilters(prev => ({ ...prev, ...patch }));
  };

  const clearFilters = () => {
    setFilters({ search: '', ticket_type: '', status: '', assigned_to_user: '' });
  };

  const activeFilterCount = [
    filters.search,
    filters.ticket_type,
    filters.status,
    filters.assigned_to_user
  ].filter(Boolean).length;

  const quickFilters = [
    {
      key: 'all',
      label: 'All',
      active: !filters.ticket_type && !filters.status && !filters.assigned_to_user,
      onClick: () => updateFilters({ ticket_type: '', status: '', assigned_to_user: '' })
    },
    {
      key: 'mine',
      label: 'Mine',
      active: filters.assigned_to_user === 'me',
      onClick: () => updateFilters({ assigned_to_user: filters.assigned_to_user === 'me' ? '' : 'me' })
    },
    {
      key: 'open',
      label: 'Open',
      active: filters.status === 'open',
      onClick: () => updateFilters({ status: filters.status === 'open' ? '' : 'open' })
    },
    {
      key: 'unassigned',
      label: 'Unassigned',
      active: filters.assigned_to_user === 'unassigned',
      onClick: () => updateFilters({ assigned_to_user: filters.assigned_to_user === 'unassigned' ? '' : 'unassigned' })
    },
    {
      key: 'cancelled',
      label: 'Cancelled',
      active: filters.status === 'cancelled',
      onClick: () => updateFilters({ status: filters.status === 'cancelled' ? '' : 'cancelled' })
    }
  ].filter(item => item.key !== 'mine' || admin?._id);

  const closeCreateModal = () => {
    if (creating) return;
    setShowCreateModal(false);
    resetCreateForm();
  };
  const showInitialOwner = ![COSTING_RATE_TICKET_TYPE, 'record_update'].includes(createForm.ticket_type) &&
    (!isProviderChangeTicketType(createForm.ticket_type) || createForm.ticket_type === 'add_provider');
  const relatedRecordFields = <div className="ticket-create-form">
    <TicketMerchantSelect search={merchantSearch} onSearch={updateMerchantSearch} matchesSelection={merchantSearchMatchesSelection}
      options={filteredMerchants} onSelect={selectCreateMerchant} disabled={creating} loading={loadingMerchants} />
    {createForm.ticket_type !== 'add_provider' && <div>
      <label className="ticket-create-label">{createForm.ticket_type === 'change_provider' ? 'Provider to update *' : createForm.ticket_type === 'remove_provider' ? 'Provider to delete *' : 'Provider'}</label>
      <div className="ticket-create-select">
        <input className="input" aria-label={createForm.ticket_type === 'change_provider' ? 'Provider to update' : createForm.ticket_type === 'remove_provider' ? 'Provider to delete' : 'Provider'}
          value={providerSearch} onChange={event => updateProviderSearch(event.target.value)}
          placeholder={!createForm.merchant_id ? 'Select merchant first' : loadingProviders ? 'Loading providers…' : 'Search provider'}
          disabled={creating || !createForm.merchant_id || loadingProviders} />
        {providerSearch.trim() && createForm.merchant_id && !providerSearchMatchesSelection && <div className="ticket-create-options">
          {filteredProviders.length ? filteredProviders.slice(0, 10).map(item => <ProviderSelectOption key={getProviderItem(item)._id}
            provider={getProviderItem(item)} disabled={creating} onClick={() => selectCreateProvider(item)}
            status="Already in merchant" statusTone="warning" />) : <span className="ticket-create-no-results">No provider found.</span>}
        </div>}
      </div>
    </div>}
    {createForm.ticket_type === 'add_provider' && <div>
      <label className="ticket-create-label">{createForm.provider_kind === 'payment_gateway' ? 'Payment gateway to assign *' : 'Provider to add *'}</label>
      <div className="ticket-create-select">
        <input className="input" aria-label="Provider to add" value={targetProviderSearch} onChange={event => updateTargetProviderSearch(event.target.value)}
          placeholder={!createForm.merchant_id ? 'Select merchant first' : 'Search active L1 provider'} disabled={creating || !createForm.merchant_id} aria-busy={loadingTargetProviders} />
        {targetProviderSearch.trim() && createForm.merchant_id && !targetProviderSearchMatchesSelection && <div className="ticket-create-options">
          {loadingTargetProviders ? <span className="ticket-create-no-results" role="status">Loading providers…</span>
            : targetProviders.length ? targetProviders.map(provider => {
              const alreadyConfigured = configuredProviderIds.has(provider._id);
              return <ProviderSelectOption key={provider._id} provider={provider}
                disabled={creating || loadingProviders || Boolean(merchantProvidersError) || alreadyConfigured}
                onClick={() => selectCreateTargetProvider(provider)}
                status={loadingProviders ? 'Checking merchant…' : merchantProvidersError || (alreadyConfigured ? 'Already in merchant' : 'Not in merchant')}
                statusTone={alreadyConfigured || merchantProvidersError ? 'warning' : 'neutral'} />;
            }) : <span className="ticket-create-no-results">No available provider found.</span>}
        </div>}
      </div>
    </div>}
  </div>;

  return (
    <div className="tickets-page">
      <div className="tickets-header">
        <div>
          <h2>Ticket queue <span className="tickets-total" aria-live="polite">{pagination.total}</span></h2>
          <p>Collect information, hand off work and review requests.</p>
        </div>
        {canCreate && <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>Create ticket</button>}
      </div>

      <section className="card tickets-table-card" aria-label="Ticket queue">
        <div className="tickets-toolbar">
          <div className="tickets-toolbar-top">
            <div className="tickets-quick-filters" aria-label="Quick ticket filters">
              {quickFilters.map(item => <button key={item.key} type="button" aria-pressed={item.active}
                className={`tickets-quick-filter${item.active ? ' active' : ''}`} onClick={item.onClick}>{item.label}</button>)}
            </div>
            <div className="tickets-search">
              <SearchIcon />
              <input className="input" aria-label="Search tickets" placeholder="Search ticket name or note" value={filters.search}
                onChange={event => updateFilters({ search: event.target.value })} />
            </div>
          </div>
          <div className="tickets-filter-grid">
            <label className="tickets-filter-field"><span>Type</span>
              <select className="input" value={filters.ticket_type} onChange={event => updateFilters({ ticket_type: event.target.value })}>
                <option value="">All types</option>
                <option value="setup">Setup</option>
                <option value="manual">Manual</option>
                <option value="record_update">Record change</option>
                <option value="l0_create">L0 creation</option>
                <option value="l1_create">Add L1 master</option>
                <option value="add_provider">Add provider</option>
                <option value="change_provider">Update provider</option>
                <option value="remove_provider">Delete provider</option>
                <option value={COSTING_RATE_TICKET_TYPE}>L1 costing rate</option>
              </select>
            </label>
            <label className="tickets-filter-field"><span>Status</span>
              <select className="input" value={filters.status} onChange={event => updateFilters({ status: event.target.value })}>
                <option value="">All statuses</option>
                {[['working', 'Working statuses'], ['approval', 'Approval statuses'], ['closed', 'Closed statuses']].map(([group, label]) => (
                  <optgroup key={group} label={label}>
                    {(workflowMeta.queue_statuses || DEFAULT_STATUSES).filter(option => (option.group ||
                      (['completed', 'rejected', 'cancelled'].includes(option.value) ? 'closed' : ['pending_approval', 'approved', 'applying'].includes(option.value) ? 'approval' : 'working')) === group)
                      .map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
            <label className="tickets-filter-field"><span>Owner</span>
              <select className="input" value={filters.assigned_to_user} onChange={event => updateFilters({ assigned_to_user: event.target.value })}>
                <option value="">All owners</option>
                {admin?._id && <option value="me">My tickets</option>}
                <option value="unassigned">Unassigned</option>
                {assignees.map(user => <option key={user._id} value={user._id}>{user.username || user.email}</option>)}
              </select>
            </label>
            {activeFilterCount > 0 && <button type="button" className="tickets-clear-btn" onClick={clearFilters}>Clear filters ({activeFilterCount})</button>}
            <span className="tickets-results" role="status">{loading ? 'Refreshing…' : `${pagination.total} ${pagination.total === 1 ? 'ticket' : 'tickets'}`}</span>
          </div>
        </div>

        <div className="tickets-table-scroll" aria-busy={loading}>
          <table className="table-striped tickets-table">
            <thead><tr>{['Ticket', 'Merchant', 'Status', 'Created by', 'Owner', 'Updated', ''].map((heading, index) => (
              <th key={index} scope="col">{heading || <span className="tickets-sr-only">Actions</span>}</th>
            ))}</tr></thead>
            <tbody>
              {tickets.length === 0 ? <tr><td colSpan="7"><div className="tickets-empty">
                <strong>{loading ? 'Loading tickets…' : 'No tickets found'}</strong>
                {!loading && <span>{activeFilterCount ? 'Try another search or clear the filters.' : 'New requests will appear here.'}</span>}
                {!loading && activeFilterCount > 0 && <button type="button" className="btn btn-secondary" onClick={clearFilters}>Clear filters</button>}
              </div></td></tr> : tickets.map(ticket => {
                const item = getTicketQueueItem(ticket, getTicketStatusLabel(ticket));
                const merchant = ticket.merchant_id;
                return <tr key={ticket._id}>
                  <td>
                    <div className="tickets-name-line">
                      <Link className="tickets-row-title" to={`/tickets/${ticket._id}`} title={ticket.title} aria-label={`View ticket ${ticket.title}`}>{item.title}</Link>
                      <span className="tickets-row-type">{item.typeLabel}</span>
                      {['high', 'urgent'].includes(ticket.priority) && <span className={`tickets-priority is-${ticket.priority}`}>{ticket.priority}</span>}
                    </div>
                    {item.connection.length > 0 && <div className="tickets-row-meta">
                      {item.connection.map((value, index) => <span key={index}>{value}</span>)}
                    </div>}
                    {(item.category || item.subCategory) && <div className="tickets-row-category">
                      {item.category && <span title={`Category: ${item.category}`}>{item.category}</span>}
                      {item.category && item.subCategory && <span className="tickets-category-divider" aria-hidden="true">/</span>}
                      {item.subCategory && <span title={`Subcategory: ${item.subCategory}`}>{item.subCategory}</span>}
                    </div>}
                    {item.detail && (!item.connection.length || ticket.ticket_type === COSTING_RATE_TICKET_TYPE) && <p className="tickets-row-description">{item.detail}</p>}
                  </td>
                  <td>{merchant?._id ? <Link className="tickets-merchant-link" to={`/merchants/${merchant._id}/view`} title={`Open merchant ${merchant.name || merchant.merchant_code}`}>
                    <span>{merchant.name || merchant.merchant_code}</span><small>{merchant.merchant_code || merchant.client_code}</small>
                  </Link> : <span className="tickets-unset">—</span>}</td>
                  <td><span className={`tickets-queue-status is-${item.tone}`} title={getTicketStatusLabel(ticket)}>{item.statusLabel}</span></td>
                  <td><TicketUser name={item.creator} fallback="Unknown" /></td>
                  <td><TicketUser name={item.owner} fallback="Unassigned" /></td>
                  <td><time className="tickets-date" dateTime={ticket.updated_at || undefined} title={item.fullDate}>{item.date}<small>{item.time}</small></time></td>
                  <td className="tickets-actions"><Link className="tickets-action-btn" to={`/tickets/${ticket._id}`} aria-label={`Open ticket ${ticket.title}`} title="Open ticket"><EyeIcon size={16} /></Link></td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
        <Pagination pagination={pagination} onPageChange={page => fetchTickets(page)} onLimitChange={limit => {
          setPagination(prev => ({ ...prev, limit }));
          fetchTickets(1, limit);
        }} loading={loading} itemName="tickets" />
      </section>

      {showCreateModal && <dialog className="card ticket-create-dialog" ref={createDialogRef} aria-labelledby="create-ticket-title"
        onCancel={event => { event.preventDefault(); closeCreateModal(); }}>
        <header className="ticket-create-header">
          <h3 id="create-ticket-title">Create ticket</h3>
          <button type="button" className="ticket-create-close" aria-label="Close create ticket" disabled={creating} onClick={closeCreateModal}>×</button>
        </header>
        <div className="ticket-create-body">
          {createError && <div role="alert" className="ticket-create-error">{createError}</div>}
          {isProviderChangeTicketType(createForm.ticket_type) && <p className="ticket-create-hint">Submit a provider change request. Finance or Superadmin approval is required before changes take effect.</p>}
          <div className="ticket-create-form">
            <div className={`ticket-create-basics${showInitialOwner ? '' : ' is-single'}`}>
              <label className="ticket-create-label">Ticket type
                <select ref={createTypeRef} className="input" value={createForm.provider_kind === 'payment_gateway' ? createForm.ticket_type.replace('_provider', '_payment_gateway') : createForm.ticket_type} onChange={event => updateCreateTicketType(event.target.value)} disabled={creating}>
                  <option value="manual">Manual ticket</option>
                  <option value="record_update">Update existing record (Merchant / L0 / L1)</option>
                  <option value="l0_create">New L0 record</option>
                  <option value="l1_create">Add L1 master provider</option>
                  <option value="add_provider">Add provider</option>
                  <option value="add_payment_gateway">Assign L1 payment gateway to merchant</option>
                  <option value="change_payment_gateway">Update merchant payment gateway</option>
                  <option value="remove_payment_gateway">Remove merchant payment gateway</option>
                  <option value="change_provider">Update provider</option>
                  <option value="remove_provider">Delete provider</option>
                  <option value={COSTING_RATE_TICKET_TYPE}>L1 costing rate change</option>
                </select>
              </label>
              {showInitialOwner && <TicketAssigneeSelect search={assigneeSearch} onSearch={updateAssigneeSearch}
                matchesSelection={assigneeSearchMatchesSelection} options={filteredAssignees} onSelect={selectCreateAssignee}
                disabled={creating} required={['l0_create', 'l1_create', 'add_provider'].includes(createForm.ticket_type)} />}
            </div>
            {createForm.ticket_type === 'manual' && <label className="ticket-create-label">Ticket name *
              <input className="input" value={createForm.title} onChange={event => updateCreateForm('title', event.target.value)} placeholder="What needs to be done?" disabled={creating} />
            </label>}
            {createForm.ticket_type === 'record_update' && <RecordUpdateFields value={recordRequest} onChange={setRecordRequest} disabled={creating} />}
            {isRecordCreationTicketType(createForm.ticket_type) && <RecordCreationFields key={createForm.ticket_type}
              level={getRecordCreationLevel(createForm.ticket_type)} grouped={createForm.ticket_type === 'l1_create'} compact
              value={recordRequest} onOptions={setRecordOptions} onChange={value => {
                if (value.module !== recordRequest.module) setProviderChangeConfig(null);
                setRecordRequest(value);
              }} disabled={creating} />}
            {createForm.ticket_type === 'l1_create' && recordRequest.module && hasPermission('merchants', 'view') && <details className="ticket-create-section">
              <summary>Link to merchant <span>Optional</span></summary>
              <TicketMerchantSelect search={merchantSearch} onSearch={updateMerchantSearch} matchesSelection={merchantSearchMatchesSelection}
                options={filteredMerchants.filter(merchant => merchant.is_active !== false)} onSelect={selectCreateMerchant} disabled={creating} loading={loadingMerchants} optional />
              {selectedMerchant && <p className="ticket-create-hint">{recordRequest.module === 'payment_gateway_master_providers'
                ? `After Finance approval, opens a payment gateway assignment ticket for ${getMerchantLabel(selectedMerchant)}. The gateway is added after assignment approval.`
                : `After approval, adds this L1 as Draft and opens a setup ticket for ${getMerchantLabel(selectedMerchant)}.`}</p>}
              {createForm.merchant_id && <details className="ticket-create-section">
                <summary>Merchant configuration</summary>
                <ProviderChangeConfigFields provider={recordProvider}
                  value={mergeProviderChangeConfig(recordProvider, providerChangeConfig || {})}
                  onChange={setProviderChangeConfig} disabled={creating} />
              </details>}
            </details>}
              {createForm.ticket_type === COSTING_RATE_TICKET_TYPE && (
                <>
                  <div>
                    <label className="ticket-create-label">L1 Provider *</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="input"
                        aria-label="L1 provider"
                        value={costingProviderSearch}
                        onChange={(e) => updateCostingProviderSearch(e.target.value)}
                        placeholder={loadingCostingProviders ? 'Loading L1 providers...' : 'Search provider, code, source, or currency...'}
                        disabled={creating}
                      />
                      {costingProviderSearch.trim() && !costingProviderSearchMatchesSelection && (
                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 30, display: 'grid', gap: '0.25rem', maxHeight: '260px', overflowY: 'auto', padding: '0.35rem', border: '1px solid var(--border-light)', borderRadius: '8px', backgroundColor: 'var(--bg-card)', boxShadow: '0 12px 28px rgba(var(--neutral-rgb), 0.12)' }}>
                          {costingProviders.slice(0, 10).length === 0 ? (
                            <div style={{ padding: '0.65rem 0.75rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>No L1 provider matches this search.</div>
                          ) : costingProviders.slice(0, 10).map(provider => (
                            <button
                              key={provider._id}
                              type="button"
                              disabled={creating}
                              onClick={() => selectCreateCostingProvider(provider)}
                              style={{ display: 'grid', gap: '0.05rem', minHeight: '42px', padding: '0.5rem 0.6rem', borderRadius: '6px', border: '1px solid transparent', backgroundColor: 'transparent', color: 'var(--text-primary)', cursor: creating ? 'not-allowed' : 'pointer', textAlign: 'left' }}
                            >
                              <span style={{ fontWeight: 900, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getProviderOptionLabel(provider)}</span>
                              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Current rate {formatRate(provider.costing_rates)}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem' }}>
                    <div>
                      <label className="ticket-create-label">Current Rate</label>
                      <input className="input" aria-label="Current rate" value={selectedCostingProvider ? formatRate(selectedCostingProvider.costing_rates) : '-'} disabled />
                    </div>
                    <div>
                      <label className="ticket-create-label">Requested Rate *</label>
                      <input className="input" aria-label="Requested rate" type="number" min="0" max="100" step="0.01" value={createForm.requested_rate} onChange={(e) => updateCreateForm('requested_rate', e.target.value)} placeholder="0.00" disabled={creating} />
                    </div>
                  </div>
                </>
              )}

            {!isRecordCreationTicketType(createForm.ticket_type) && !['record_update', COSTING_RATE_TICKET_TYPE].includes(createForm.ticket_type) && (
              createForm.ticket_type === 'manual' ? <details className="ticket-create-section"><summary>Related records <span>Optional</span></summary>{relatedRecordFields}</details> : relatedRecordFields
            )}
            {['add_provider', 'change_provider'].includes(createForm.ticket_type) && providerToConfigure && providerChangeConfig && <details className="ticket-create-section">
              <summary>Provider configuration</summary>
              <ProviderChangeConfigFields provider={providerToConfigure} value={providerChangeConfig} onChange={setProviderChangeConfig}
                disabled={creating} allowStatusChange={createForm.ticket_type === 'change_provider'} />
            </details>}
            {createForm.ticket_type === COSTING_RATE_TICKET_TYPE ? <label className="ticket-create-label">Reason
              <textarea className="input" rows="2" value={createForm.description} onChange={event => updateCreateForm('description', event.target.value)} placeholder="Why is this rate changing?" disabled={creating} />
            </label> : <details className="ticket-create-section">
              <summary>Notes & scheduling <span>Optional</span></summary>
              <label className="ticket-create-label">Note<textarea className="input" rows="2" value={createForm.description}
                onChange={event => updateCreateForm('description', event.target.value)} placeholder="Context for the next person" disabled={creating} /></label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem' }}>
                <div>
                  <label className="ticket-create-label">Priority</label>
                  <select className="input" aria-label="Priority" value={createForm.priority} onChange={(e) => updateCreateForm('priority', e.target.value)} disabled={creating}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="ticket-create-label">Due Date</label>
                  <input className="input" aria-label="Due date" type="date" value={createForm.due_date} onChange={(e) => updateCreateForm('due_date', e.target.value)} disabled={creating} />
                </div>
              </div>

            </details>}
          </div>
        </div>
        <footer className="ticket-create-footer">
          {['l1_create', 'add_provider', 'change_provider'].includes(createForm.ticket_type) && <span>Other details can be completed later.</span>}
          <button type="button" className="btn btn-secondary" disabled={creating} onClick={closeCreateModal}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={creating} onClick={handleCreateTicket}>{creating ? 'Creating…' : 'Create ticket'}</button>
        </footer>
      </dialog>}

    </div>
  );
};

export default Tickets;
